import { useMemo, useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import { z } from 'zod';
import { organisationsQuery } from '@/features/crm/queries';
import { BILLING_MODELS, ENGAGEMENT_STATUSES, createEngagement } from '@mkcore/engagements/schema';
import { Badge, Button, Checkbox, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, EmptyState, ErrorState, Field, Input, NativeSelect, PageHeader, Skeleton } from '@mkcore/ui';
import { ApiError, newIdempotencyKey } from '@/lib/api';
import { useActionMutation, useErrorMessage } from '@/lib/mutation';
import { useCan } from '@/lib/session';
import { AgreementsEnding } from '@/features/reporting/agreements-ending';
import { engagementsQuery } from '@/features/engagements/queries';
import { hoursToMinutes, inputToCents } from '@/features/engagements/money';
import { EngagementStatusBadge, ModelLabel } from '@/features/engagements/labels';
import { DataTable, type ColumnDef } from '@/components/data-table';
import { formatDate } from '@/lib/format';
import { EngagementsOverview } from '@/features/overviews';

const search = z.object({
  q: z.string().optional(),
  status: z.enum(ENGAGEMENT_STATUSES).optional(),
  billingModel: z.enum(BILLING_MODELS).optional(),
  organisationId: z.string().optional(),
  archived: z.boolean().optional(),
});

export const Route = createFileRoute('/_app/engagements/')({ validateSearch: search, component: Engagements });

/** The agreements with clients: billing model, status, budget (CO-0168, CO-0271). */
function Engagements() {
  const { t } = useTranslation();
  const s = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const message = useErrorMessage();
  const canCreate = useCan('engagements:create');
  const [open, setOpen] = useState(false);
  const q = useInfiniteQuery(
    engagementsQuery({
      q: s.q || undefined,
      status: s.status ? [s.status] : undefined,
      billingModel: s.billingModel ? [s.billingModel] : undefined,
      organisationId: s.organisationId || undefined,
      includeArchived: s.archived || undefined,
    }),
  );
  const rows = useMemo(() => q.data?.pages.flatMap((p) => p.items) ?? [], [q.data]);
  const columns = useMemo<ColumnDef<(typeof rows)[number]>[]>(
    () => [
      {
        id: 'name',
        label: t('common.name'),
        value: (e) => e.name,
        cell: (e) => (
          <>
            <Link to="/engagements/$id" params={{ id: e.id }} className="font-bold hover:underline">
              {e.name}
            </Link>
            {e.archivedAt && (
              <Badge tone="neutral" className="ml-2">
                {t('org.archived')}
              </Badge>
            )}
          </>
        ),
      },
      {
        id: 'organisation',
        label: t('org.eyebrow'),
        groupable: true,
        value: (e) => e.organisationName,
        cell: (e) => (
          <Link to="/org/$id" params={{ id: e.organisationId }} className="hover:underline">
            {e.organisationName}
          </Link>
        ),
      },
      {
        id: 'billingModel',
        label: t('engagements.fields.billingModel'),
        type: 'enum',
        groupable: true,
        options: BILLING_MODELS.map((m) => ({ value: m, label: t(`engagements.model.${m}`) })),
        value: (e) => e.billingModel,
        cell: (e) => <ModelLabel model={e.billingModel} />,
        className: 'hidden md:table-cell',
      },
      {
        id: 'status',
        label: t('common.status'),
        type: 'enum',
        groupable: true,
        options: ENGAGEMENT_STATUSES.map((x) => ({ value: x, label: t(`engagements.status.${x}`) })),
        value: (e) => e.status,
        cell: (e) => <EngagementStatusBadge status={e.status} />,
      },
      { id: 'startsOn', label: t('engagements.fields.startsOn'), type: 'date', value: (e) => e.startsOn, cell: (e) => (e.startsOn ? formatDate(e.startsOn) : '–'), className: 'hidden lg:table-cell tabular text-fg-2' },
      { id: 'endsOn', label: t('engagements.fields.endsOn'), type: 'date', value: (e) => e.endsOn, cell: (e) => (e.endsOn ? formatDate(e.endsOn) : '–'), className: 'hidden lg:table-cell tabular text-fg-2' },
      { id: 'budgetHours', label: t('engagements.fields.budgetHours'), type: 'number', hidden: true, value: (e) => (e.budgetMinutes === null ? null : e.budgetMinutes / 60), cell: (e) => (e.budgetMinutes === null ? '–' : e.budgetMinutes / 60), className: 'text-right tabular' },
      { id: 'purchaseOrder', label: t('engagements.fields.purchaseOrder'), hidden: true, value: (e) => e.purchaseOrder, cell: (e) => e.purchaseOrder ?? '–' },
    ],
    [t],
  );
  const set = (patch: Partial<z.infer<typeof search>>) => void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });

  return (
    <>
      <PageHeader
        title={t('engagements.title')}
        description={t('engagements.description')}
        actions={
          canCreate && (
            <Button onClick={() => setOpen(true)}>
              <Plus /> {t('engagements.new')}
            </Button>
          )
        }
      />
      <EngagementsOverview />
      <div className="mb-4">
        <AgreementsEnding card />
      </div>
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <Field label={t('common.search')} className="w-64">
          {(p) => <Input {...p} value={s.q ?? ''} onChange={(e) => set({ q: e.target.value || undefined })} />}
        </Field>
        <Field label={t('common.status')} className="w-40">
          {(p) => (
            <NativeSelect {...p} value={s.status ?? ''} onChange={(e) => set({ status: (e.target.value || undefined) as never })}>
              <option value="">{t('common.all')}</option>
              {ENGAGEMENT_STATUSES.map((v) => (
                <option key={v} value={v}>
                  {t(`engagements.status.${v}`)}
                </option>
              ))}
            </NativeSelect>
          )}
        </Field>
        <Field label={t('engagements.fields.billingModel')} className="w-44">
          {(p) => (
            <NativeSelect {...p} value={s.billingModel ?? ''} onChange={(e) => set({ billingModel: (e.target.value || undefined) as never })}>
              <option value="">{t('common.all')}</option>
              {BILLING_MODELS.map((v) => (
                <option key={v} value={v}>
                  {t(`engagements.model.${v}`)}
                </option>
              ))}
            </NativeSelect>
          )}
        </Field>
        <label className="flex items-center gap-2 pb-2 text-sm">
          <Checkbox checked={!!s.archived} onCheckedChange={(v) => set({ archived: v === true || undefined })} /> {t('org.includeArchived')}
        </label>
      </div>
      {q.isError ? (
        <ErrorState title={t('errors.generic')} description={message(q.error)} />
      ) : q.isLoading ? (
        <Skeleton className="h-64" />
      ) : !rows.length ? (
        <EmptyState title={t('engagements.empty')} description={canCreate ? t('engagements.emptyHint') : undefined} />
      ) : (
        <DataTable listKey="engagement.engagement.list" columns={columns} rows={rows} rowKey={(e) => e.id} onOpen={(e) => void navigate({ to: '/engagements/$id', params: { id: e.id } })} partial={q.hasNextPage} onNeedAll={() => q.hasNextPage && !q.isFetchingNextPage && void q.fetchNextPage()} />
      )}
      {q.hasNextPage && (
        <div className="mt-3 flex justify-center">
          <Button variant="secondary" loading={q.isFetchingNextPage} onClick={() => void q.fetchNextPage()}>
            {t('common.more')}
          </Button>
        </div>
      )}
      <NewEngagement open={open} organisationId={s.organisationId} onClose={() => setOpen(false)} />
    </>
  );
}

export function NewEngagement({ open, organisationId, onClose }: { open: boolean; organisationId?: string; onClose: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const message = useErrorMessage();
  const orgs = useInfiniteQuery(organisationsQuery({ limit: 200 }));
  const orgItems = orgs.data?.pages.flatMap((p) => p.items) ?? [];
  const [key] = useState(newIdempotencyKey);
  const [org, setOrg] = useState(organisationId ?? '');
  const [name, setName] = useState('');
  const [model, setModel] = useState<(typeof BILLING_MODELS)[number]>('tm');
  const [fixed, setFixed] = useState('');
  const [period, setPeriod] = useState<'month' | 'quarter' | 'year'>('month');
  const [hours, setHours] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const create = useActionMutation(createEngagement, {
    invalidate: [['engagement']],
    silentErrors: true,
    onSuccess: (e) => {
      onClose();
      void navigate({ to: '/engagements/$id', params: { id: e.id } });
    },
  });
  const submit = () =>
    create.mutate(
      {
        input: {
          organisationId: org,
          name,
          billingModel: model,
          ...(model === 'fixed' ? { fixedAmountCents: inputToCents(fixed) } : {}),
          ...(model === 'retainer' ? { retainerPeriod: period, retainerMinutes: hoursToMinutes(hours) } : {}),
        },
        key,
      },
      { onError: (e) => setErrors(e instanceof ApiError ? { ...e.fieldErrors(), _: message(e) } : { _: message(e) }) },
    );
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent closeLabel={t('common.close')}>
        <DialogHeader>
          <DialogTitle>{t('engagements.new')}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-3">
          <Field label={t('org.eyebrow')} error={errors.organisationId}>
            {(p) => (
              <NativeSelect {...p} value={org} onChange={(e) => setOrg(e.target.value)}>
                <option value="">–</option>
                {orgItems.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </NativeSelect>
            )}
          </Field>
          <Field label={t('common.name')} error={errors.name}>
            {(p) => <Input {...p} autoFocus value={name} onChange={(e) => setName(e.target.value)} />}
          </Field>
          <Field label={t('engagements.fields.billingModel')} hint={t(`engagements.modelHint.${model}`)}>
            {(p) => (
              <NativeSelect {...p} value={model} onChange={(e) => setModel(e.target.value as typeof model)}>
                {BILLING_MODELS.map((v) => (
                  <option key={v} value={v}>
                    {t(`engagements.model.${v}`)}
                  </option>
                ))}
              </NativeSelect>
            )}
          </Field>
          {model === 'fixed' && (
            <Field label={t('engagements.fields.fixedAmount')} error={errors.fixedAmountCents}>
              {(p) => <Input {...p} inputMode="decimal" value={fixed} onChange={(e) => setFixed(e.target.value)} placeholder="12500.00" />}
            </Field>
          )}
          {model === 'retainer' && (
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label={t('engagements.fields.retainerPeriod')}>
                {(p) => (
                  <NativeSelect {...p} value={period} onChange={(e) => setPeriod(e.target.value as typeof period)}>
                    {(['month', 'quarter', 'year'] as const).map((v) => (
                      <option key={v} value={v}>
                        {t(`engagements.period.${v}`)}
                      </option>
                    ))}
                  </NativeSelect>
                )}
              </Field>
              <Field label={t('engagements.fields.retainerHours')} error={errors.retainerMinutes}>
                {(p) => <Input {...p} inputMode="decimal" value={hours} onChange={(e) => setHours(e.target.value)} placeholder="20" />}
              </Field>
            </div>
          )}
          {errors._ && (
            <p role="alert" className="text-sm text-danger">
              {errors._}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button loading={create.isPending} disabled={!org || !name.trim()} onClick={submit}>
            {t('common.create')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
